import { Label } from 'components/ui/label';
import { RadioGroup, RadioGroupItem } from 'components/ui/radio-group';
import React, { useEffect, useState } from 'react';
import InfoAlert from './infoAlert';
import {
  Control,
  FieldValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';
import { PAYMENT_CONFIG } from 'state/paymentConfig/type';
import ConfigToggle from './configToggle';
import { FieldConfig } from './cardReaderSection';
import { Input } from 'components/ui/input';
import ConfigRange from './configRange';

interface Iprops {
  config: PAYMENT_CONFIG.IProcessorConfiguration;
  registerProp: UseFormRegister<FieldValues>;
  default_value: string;
  watch: UseFormWatch<FieldValues>;
  setValueProp: UseFormSetValue<FieldValues>;
  control: Control<FieldValues, any>;
  percentValue?: FieldConfig;
}
const ConfigPreTipping = ({
  config,
  registerProp,
  default_value,
  watch,
  setValueProp,
  percentValue,
  control,
}: Iprops) => {
  const [lowTip, setLowTip] = useState(5);
  const [midTip, setMidTip] = useState(10);
  const [highTip, setHighTip] = useState(15);
  const [tipPercentValue, setTipPercentValue] = useState<string>();

  useEffect(() => {
    if (midTip <= lowTip) {
      setMidTip(lowTip + 5);
    }
    if (highTip <= midTip) {
      if (midTip + 5 < 100) {
        setHighTip(midTip + 5);
      } else {
        setHighTip(100);
      }
    }
    if (lowTip > 90) {
      setLowTip(90);
    }
    if (midTip > 95) {
      setMidTip(95);
    }
  }, [lowTip, midTip, highTip]);

  useEffect(() => {
    if (percentValue?.itemConfig?.defaultValue) {
      const values = percentValue.itemValue?.value.split(',');
      setLowTip(parseInt(values?.[0] || '5', 10));
      setMidTip(parseInt(values?.[1] || '10', 10));
      setHighTip(parseInt(values?.[2] || '15', 10));
      setValueProp(
        `${percentValue?.itemConfig?.code}`,
        percentValue?.itemConfig?.defaultValue
      );
      setTipPercentValue(percentValue?.itemConfig?.defaultValue);
    }
  }, [percentValue]);

  useEffect(() => {
    const code = percentValue?.itemConfig?.code;
    if (!code) return;
    const tipString = `${lowTip},${midTip},${highTip}`;
    setTipPercentValue(tipString);
    setValueProp(code, tipString, { shouldDirty: true });
  }, [lowTip, midTip, highTip, percentValue?.itemConfig?.code]);

  const { ref: hiddenRef, ...hiddenField } = registerProp(`${percentValue?.itemConfig?.code}`);

  return (
    <>
      <div className="mb-3 mr-3">
        <ConfigToggle config={config} control={control} marginBottom={false} />

        <Input
          ref={hiddenRef}
          {...hiddenField}
          type="text"
          id={`config-${percentValue?.itemConfig?.code}`}
          value={tipPercentValue || 1}
          style={{ display: 'none' }}
        />

        {watch(`${config?.code}`) === 'true' && (
          <>
            <InfoAlert className="px-4 py-4 gap-3">
              For both the selections below, the dollar value would also be
              accompanied with the percentage value
            </InfoAlert>
            <RadioGroup defaultValue="percent_range" className="ml-3 mt-2">
              <div className="flex items-center gap-2">
                <RadioGroupItem value="percent_range" id="percent_range" />
                <Label htmlFor="percent_range" className="cursor-pointer">
                  By percentage range
                </Label>
              </div>
            </RadioGroup>
            <div className="ml-4 pl-1 mt-3">
              <Label htmlFor={`${config?.code}`} className="text-sm font-medium text-gray-blue-80 mb-1">
                {config?.displayName}
              </Label>
              <ConfigRange
                fieldName={'Low'}
                min={1}
                max={100}
                code={percentValue?.itemConfig?.code || ''}
                value={lowTip}
                onChangeRange={(value) => setLowTip(parseInt(value))}
                onChangeInput={(value) =>
                  parseInt(value) < 90 && setLowTip(parseInt(value))
                }
              />
              <ConfigRange
                fieldName={'Mid'}
                min={1}
                max={100}
                code={percentValue?.itemConfig?.code || ''}
                value={midTip || lowTip + 5}
                onChangeRange={(value) => setMidTip(parseInt(value))}
                onChangeInput={(value) =>
                  parseInt(value) < 100 && setMidTip(parseInt(value))
                }
              />
              <ConfigRange
                fieldName={'High'}
                min={1}
                max={100}
                code={percentValue?.itemConfig?.code || ''}
                value={highTip || midTip + 5}
                onChangeRange={(value) => setHighTip(parseInt(value))}
                onChangeInput={(value) =>
                  parseInt(value) > 0 &&
                  parseInt(value) < 101 &&
                  setHighTip(parseInt(value))
                }
              />
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default ConfigPreTipping;
